// Onboarding diagnostic. Starts a coverage diagnostic (one MCQ per topic) that
// runs through the normal exam flow and seeds bins + the SM-2 queue on submit.
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';
import { Button, Card, StudentPage, StudentPageHeader } from '@/components/ds';
import { fontPx } from '@/lib/fontSize';

interface DiagStatus {
  completed: boolean;
  paperId?: string;
}

export function DiagnosticPage() {
  const navigate = useNavigate();
  const status = useQuery({
    queryKey: ['diagnostic', 'status'],
    queryFn: () => apiGet<DiagStatus>('/diagnostic/status'),
  });

  const start = useMutation({
    mutationFn: () => apiPost<{ studentPaperId: string; questionCount: number }>('/diagnostic', {}),
    onSuccess: (d) => navigate(`/exam/${d.studentPaperId}`),
  });

  const completed = status.data?.completed;

  return (
    <StudentPage>
      <StudentPageHeader
        title="Your starting point"
        subtitle="A short diagnostic — one quick question per topic. We grade it instantly and sort every concept into a colour, so your daily reviews are personalised from day one. No marks are ever shared."
      />

      <Card>
        <div className="flex flex-col items-start gap-3 p-1">
          {completed ? (
            <>
              <span
                className="inline-block rounded-full px-3 py-1 text-xs font-semibold"
                style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}
              >
                Diagnostic complete ✓
              </span>
              <p className="text-ink">
                Your concepts are mapped. Head to Today to start reviewing.
              </p>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/today')}>Go to Today →</Button>
                <Button variant="ghost" disabled={start.isPending} onClick={() => start.mutate()}>
                  {start.isPending ? 'Starting…' : 'Retake diagnostic'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-ink">Takes about 10–15 minutes. You can pause and come back.</p>
              <Button
                style={{ padding: '12px 22px', fontSize: fontPx(15) }}
                disabled={start.isPending}
                onClick={() => start.mutate()}
              >
                {start.isPending ? 'Building your diagnostic…' : 'Start diagnostic →'}
              </Button>
            </>
          )}
          {start.isError && (
            <p className="text-sm text-danger">Couldn’t start the diagnostic. Please try again.</p>
          )}
        </div>
      </Card>
    </StudentPage>
  );
}
